import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
const money = (v) => (v === null || v === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v));
const slug = (s) => (s || "unmatched").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function SourceForm({ channels, initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [picked, setPicked] = useState(initial?.channels || []);
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const toggle = (c) => setPicked((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const submit = () => onSave({ name, channels: picked, tags: tags.split(",").map((t) => t.trim()).filter(Boolean) });
  return (
    <div className="border border-zinc-200 rounded-lg p-3 space-y-3 bg-zinc-50" data-testid="source-form">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Source name (e.g. Facebook Ads)"
        className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" data-testid="source-name-input" />
      <div>
        <p className="text-xs text-zinc-600 mb-1">Count leads auto-detected as:</p>
        <div className="flex flex-wrap gap-1.5">
          {channels.map((c) => (
            <button key={c} type="button" onClick={() => toggle(c)} data-testid={`source-channel-${slug(c)}`}
              className={`px-2 py-1 rounded-full text-xs border transition-colors ${picked.includes(c) ? "bg-[#207024] text-white border-[#207024]" : "bg-white text-zinc-700 border-zinc-300"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs text-zinc-600 mb-1">…or whose link tag (utm_source) is:</p>
        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. nextdoor, mailer_fall"
          className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" data-testid="source-tags-input" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-2 text-sm text-zinc-600" data-testid="source-cancel-btn">Cancel</button>
        <button onClick={submit} disabled={!name.trim()} className="px-4 py-2 text-sm bg-[#207024] text-white rounded-lg disabled:opacity-50" data-testid="source-save-btn">Save source</button>
      </div>
    </div>
  );
}

function SourceRow({ source, spend, onSpend, onEdit, onDelete }) {
  const match = [...(source.channels || []), ...(source.tags || []).map((t) => `tag: ${t}`)].join(" · ") || "No matching set — leads won't count here";
  return (
    <div className="flex items-center gap-2 py-2 border-b border-zinc-100 last:border-0" data-testid={`source-row-${source.source_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-900 truncate">{source.name}</div>
        <div className="text-[11px] text-zinc-500 truncate">{match}</div>
      </div>
      <div className="relative w-28">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
        <input type="number" value={spend ?? ""} onChange={(e) => onSpend(e.target.value)} placeholder="0"
          className="w-full pl-5 pr-2 py-2 border border-zinc-300 rounded-lg text-sm" data-testid={`source-spend-${source.source_id}`} />
      </div>
      <button onClick={onEdit} className="p-1.5 text-zinc-500 hover:text-zinc-900" aria-label="Edit source" data-testid={`source-edit-${source.source_id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={onDelete} className="p-1.5 text-zinc-500 hover:text-red-600" aria-label="Delete source" data-testid={`source-delete-${source.source_id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function SourceReport({ report, touch, setTouch }) {
  const rows = (report?.rows || []).filter((r) => r.source_id || r.leads || r.appointments || r.closed_deals);
  const cols = [["spend", "Spend", money], ["leads", "Leads"], ["appointments", "Appts"], ["closed_deals", "Closed"], ["revenue", "Revenue", money],
    ["cost_per_lead", "Cost/Lead", money], ["cost_per_appointment", "Cost/Appt", money], ["cost_per_close", "Cost/Close", money],
    ["roas", "ROAS", (v) => (v == null ? "—" : `${v.toFixed(2)}x`)]];
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-6 shadow-sm" data-testid="source-report">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-zinc-900">Results by source</h2>
        <div className="flex bg-zinc-100 rounded-full p-0.5 text-xs">
          {["first", "last"].map((t) => (
            <button key={t} onClick={() => setTouch(t)} data-testid={`touch-toggle-${t}`}
              className={`px-3 py-1 rounded-full transition-colors ${touch === t ? "bg-white shadow text-zinc-900 font-semibold" : "text-zinc-600"}`}>
              {t === "first" ? "First click" : "Last click"}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
              <th className="py-2 pr-3">Source</th>{cols.map(([k, l]) => <th key={k} className="py-2 pr-3">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source_id || "none"} className={`border-b border-zinc-100 ${r.source_id ? "" : "text-zinc-500"}`} data-testid={`source-report-row-${r.source_id || "unmatched"}`}>
                <td className="py-2 pr-3 font-medium">{r.name}</td>
                {cols.map(([k, , f]) => <td key={k} className="py-2 pr-3">{f ? f(r[k]) : r[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">Leads and appointments created this month; closed = invoices paid this month. "{touch === "first" ? "First" : "Last"} click" decides which ad gets credit.</p>
    </div>
  );
}

export function MarketingSources({ month, monthLabel, onSaved, onCountChange }) {
  const [sources, setSources] = useState([]);
  const [channels, setChannels] = useState([]);
  const [report, setReport] = useState(null);
  const [touch, setTouch] = useState("first");
  const [spends, setSpends] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([axios.get(`${API}/marketing/sources`, auth()),
      axios.get(`${API}/marketing/source-report?month=${month}&touch=${touch}`, auth())]);
    setSources(s.data.sources); setChannels(s.data.channels); setReport(r.data);
    onCountChange?.(s.data.sources.length);
    setSpends(Object.fromEntries(r.data.rows.filter((x) => x.source_id).map((x) => [x.source_id, x.spend ? String(x.spend) : ""])));
  }, [month, touch, onCountChange]);
  useEffect(() => { load().catch(() => toast.error("Failed to load spend sources")); }, [load]);

  const saveSource = async (body) => {
    try {
      if (editing === "new") await axios.post(`${API}/marketing/sources`, body, auth());
      else await axios.put(`${API}/marketing/sources/${editing.source_id}`, body, auth());
      setEditing(null); toast.success("Source saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save source"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? Its spend will no longer count.`)) return;
    try {
      await axios.delete(`${API}/marketing/sources/${s.source_id}`, auth());
      toast.success("Source deleted"); load(); onSaved?.();
    } catch (e) { toast.error("Failed to delete source"); }
  };
  const saveSpend = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/marketing/source-spend`, { month, spends: sources.map((s) => ({ source_id: s.source_id, amount: parseFloat(spends[s.source_id]) || 0 })) }, auth());
      toast.success("Ad spend saved!"); load(); onSaved?.();
    } catch (e) { toast.error("Failed to save ad spend"); } finally { setSaving(false); }
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-6 shadow-sm" data-testid="spend-sources">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-zinc-700">Ad Spend by Source — {monthLabel}</label>
          {!editing && (
            <button onClick={() => setEditing("new")} className="flex items-center gap-1 text-sm font-semibold text-[#207024]" data-testid="add-source-btn">
              <Plus className="w-4 h-4" /> Add source
            </button>
          )}
        </div>
        {editing && <SourceForm key={editing.source_id || "new"} channels={channels} initial={editing === "new" ? null : editing} onSave={saveSource} onCancel={() => setEditing(null)} />}
        {!sources.length && !editing && <p className="text-xs text-zinc-500">Add a source (e.g. Facebook Ads, Google Ads, Nextdoor) to track spend and results separately.</p>}
        {sources.map((s) => (
          <SourceRow key={s.source_id} source={s} spend={spends[s.source_id]} onSpend={(v) => setSpends({ ...spends, [s.source_id]: v })}
            onEdit={() => setEditing(s)} onDelete={() => remove(s)} />
        ))}
        {sources.length > 0 && (
          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-zinc-600" data-testid="source-spend-total">Total: <b>{money(sources.reduce((a, s) => a + (parseFloat(spends[s.source_id]) || 0), 0))}</b></span>
            <button onClick={saveSpend} disabled={saving} className="px-6 py-2.5 bg-[#207024] text-white font-semibold rounded-lg disabled:opacity-50" data-testid="save-source-spend-btn">
              {saving ? "Saving..." : "Save spend"}
            </button>
          </div>
        )}
      </div>
      {sources.length > 0 && <SourceReport report={report} touch={touch} setTouch={setTouch} />}
    </>
  );
}
